import { Link } from 'react-router-dom'
import { RotateCcw } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { SUPPORT_EMAIL } from '@/lib/contact'

/**
 * Branded fallback for a render throw. Deliberately offers three exits — retry,
 * home, and a way to reach a human — because the one thing a visitor cannot do
 * from a blank page is tell anyone it happened.
 */
export function RouteErrorFallback({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()

  return (
    <div
      role="alert"
      className="min-h-[60vh] flex flex-col items-center justify-center px-6 py-20 text-center"
    >
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground">
        {t.errorBoundary.title}
      </h1>
      <p className="mt-3 text-base text-muted-foreground max-w-md leading-relaxed">
        {t.errorBoundary.message}
      </p>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        <button
          onClick={onRetry}
          className="inline-flex items-center gap-2 min-h-11 px-6 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 gentle-animation shadow-lg shadow-primary/20 cursor-pointer"
        >
          <RotateCcw className="w-4 h-4" aria-hidden="true" />
          {t.errorBoundary.retry}
        </button>
        <Link
          to="/"
          className="inline-flex items-center min-h-11 px-6 rounded-lg border-2 border-primary text-primary font-semibold hover:bg-primary/5 gentle-animation"
        >
          {t.errorBoundary.backHome}
        </Link>
      </div>
      <a
        href={`mailto:${SUPPORT_EMAIL}`}
        className="mt-6 text-sm font-medium text-muted-foreground underline underline-offset-4 hover:text-primary-text gentle-animation"
      >
        {t.errorBoundary.contact}
      </a>
    </div>
  )
}
